import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { trackEvent } from '@/lib/gtm';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'http://localhost:5173';

interface SocialLoginButtonsProps {
    mode: 'login' | 'signup';
    disabled?: boolean;
    onError?: (message: string) => void;
    /** For signup mode: whether terms have been accepted */
    termsAccepted?: boolean;
    onBeforeOAuth?: () => void;
}

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4" />
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853" />
        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05" />
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335" />
    </svg>
);

const MicrosoftIcon = () => (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
);

export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
    mode,
    disabled = false,
    onError,
    termsAccepted = true,
    onBeforeOAuth,
}) => {
    const [loadingProvider, setLoadingProvider] = useState<'google' | 'azure' | null>(null);

    const handleSocialLogin = async (provider: 'google' | 'azure') => {
        if (mode === 'signup' && !termsAccepted) {
            onError?.('Você precisa aceitar os Termos de Uso e a Política de Privacidade antes de continuar.');
            return;
        }

        onBeforeOAuth?.();
        setLoadingProvider(provider);

        try {
            const providerLabel = provider === 'google' ? 'google' : 'microsoft';
            trackEvent(mode === 'login' ? 'login' : 'sign_up', { 
                provider: providerLabel,
                method: 'social_initiated'
            });

            const { error } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo: `${SITE_URL}/auth/callback`,
                    scopes: provider === 'azure' ? 'email openid profile' : undefined,
                    queryParams: provider === 'azure' ? {
                        prompt: 'select_account',
                    } : undefined,
                },
            });

            if (error) {
                console.error(`[social-login] ${provider} error:`, error);
                if (error.message.includes('Provider not found')) {
                    onError?.(`Login com ${providerLabel === 'google' ? 'Google' : 'Microsoft'} não está configurado. Contate o suporte.`);
                } else {
                    onError?.('Não foi possível iniciar o login. Tente novamente.');
                }
                setLoadingProvider(null);
            }
            // If no error, user is being redirected — keep loading state
        } catch (err) {
            console.error(`[social-login] Unexpected error:`, err);
            onError?.('Ocorreu um erro inesperado. Tente novamente.');
            setLoadingProvider(null);
        }
    };

    const isDisabled = disabled || loadingProvider !== null;

    const buttonBaseClass = `
        group flex items-center justify-center gap-2.5 w-full py-2.5 px-4 rounded-xl font-bold text-sm
        border transition-all duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
    `;

    return (
        <div className="space-y-2.5">
            {/* Google Button */}
            <button
                type="button"
                onClick={() => handleSocialLogin('google')}
                disabled={isDisabled}
                className={`${buttonBaseClass} bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:shadow-sm`}
            >
                {loadingProvider === 'google' ? (
                    <div className="w-[18px] h-[18px] border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" />
                ) : (
                    <GoogleIcon />
                )}
                {mode === 'login' ? 'Continuar com Google' : 'Cadastrar com Google'}
            </button>

            {/* Microsoft Button */}
            <button
                type="button"
                onClick={() => handleSocialLogin('azure')}
                disabled={isDisabled}
                className={`${buttonBaseClass} bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:shadow-sm`}
            >
                {loadingProvider === 'azure' ? (
                    <div className="w-[18px] h-[18px] border-2 border-slate-300 border-t-[#00A4EF] rounded-full animate-spin" />
                ) : (
                    <MicrosoftIcon />
                )}
                {mode === 'login' ? 'Continuar com Microsoft' : 'Cadastrar com Microsoft'}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 py-1">
                <div className="h-px bg-slate-200 flex-1"></div>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                    {mode === 'login' ? 'ou' : 'ou cadastre-se com e-mail'}
                </span>
                <div className="h-px bg-slate-200 flex-1"></div>
            </div>
        </div>
    );
};
